// « Livreurs » (design 3): every rider on one sortable line, the accounts as
// two lines on top. A row opens the rider's file (design 2).
import { useMemo, useState } from 'react';
import { S } from '../../lib/strings';
import { fmtN, xaf } from '../../lib/format';
import { blocked, dayIn, longDay, netName, signedXaf, sortRiders, whenSaid } from '../../lib/agences';
import type { AgAgency, AgRider, Agences } from '../../lib/types';
import { Ic } from '../Icon';
import { AccountPill, Av, MiniBars, Net, RiderPill, Stars, last7 } from './Bits';

type Chip = 'live' | 'inst' | 'todo' | null;
type SortBy = 'rating' | 'trips' | 'cancelled' | 'cash';

function AccountLine({ a, onAccount }: { a: AgAgency; onAccount(id: string): void }) {
  const t = a.topups[0];
  const when = a.paused ? S.ag.tbl.pausedNow
    : a.balance < 0 ? S.ag.tbl.belowSince(a.below_zero_since ? longDay(a.below_zero_since) : '') + ' ' + (a.days_to_pause != null ? S.ag.tbl.pause(dayIn(a.days_to_pause)) : '')
      : (a.days_to_zero != null ? S.ag.tbl.zeroOn(dayIn(a.days_to_zero)) + ' ' : '') + (a.days_to_pause != null ? S.ag.tbl.pause(dayIn(a.days_to_pause)) : '');
  return (
    <div className="ag-acc">
      <div className="ag-nm"><b>{a.name}</b><span className="muted">{[a.zones.join(' · '), S.ag.tbl.riders(a.riders.length)].filter(Boolean).join(' · ')}</span></div>
      <div><div className="ag-bl num">{signedXaf(a.balance).replace(' XAF', '')}<small>XAF</small></div><AccountPill a={a} /></div>
      <div className="ag-wh">{when}<br /><span className="muted">{S.ag.tbl.perDay(a.fee_pct, xaf(a.fee_per_day))}</span></div>
      <div className="ag-tp">{t ? <><Net p={t.provider} s={30} /><div>{xaf(t.amount)} · {S.fin.st[t.status]}<small className="muted">{netName(t.provider)} · {whenSaid(t.at)}</small></div></> : <span className="muted">{S.ag.tbl.noTopup}</span>}</div>
      <button className="btn sm" onClick={() => onAccount(a.agency_id)}>{S.ag.tbl.seeAccount}</button>
    </div>
  );
}

const yes = (ok: boolean, no: string) => (ok ? <span className="ag-ok" aria-label="ok"><Ic n="check" s="xs" /></span> : <span className="ag-no">{no}</span>);

export function RidersTable({ data, onRider, onAccount, selected }: { data: Agences; onRider(id: string): void; onAccount(id: string): void; selected?: string | null }) {
  const [ag, setAg] = useState<string | null>(null);
  const [chip, setChip] = useState<Chip>(null);
  const [by, setBy] = useState<SortBy>('rating');
  const all = useMemo(() => data.agencies.flatMap((a) => a.riders.map((r) => ({ a, r }))), [data]);
  const rows = sortRiders(all.filter(({ a, r }) => (!ag || a.agency_id === ag)
    && (chip !== 'live' || r.live) && (chip !== 'inst' || r.can_install) && (chip !== 'todo' || blocked(r) || r.state === 'invited')), by);
  const n = (f: (r: AgRider) => boolean) => all.filter(({ a, r }) => (!ag || a.agency_id === ag) && f(r)).length;
  const reasonNo = (r: AgRider) => (r.missing.length >= 3 || !r.missing.length ? S.ag.tbl.toAdd : S.ag.tbl.lacks(r.missing.map((k) => S.ag.tbl.short[k]).join(', ')));
  return (
    <>
      <div className="card ag-accs">{data.agencies.map((a) => <AccountLine key={a.agency_id} a={a} onAccount={onAccount} />)}</div>
      <div className="ag-ctl">
        <div className="ag-l">
          {data.agencies.length > 1 && (
            <div className="seg" role="group" aria-label={S.ag.tbl.all}>
              <button aria-pressed={!ag} onClick={() => setAg(null)}>{S.ag.tbl.all}</button>
              {data.agencies.map((a) => <button key={a.agency_id} aria-pressed={ag === a.agency_id} onClick={() => setAg(a.agency_id)}>{a.name}</button>)}
            </div>
          )}
          <div className="ag-chips">
            {([['live', S.ag.tbl.live, n((r) => r.live)], ['inst', S.ag.tbl.inst, n((r) => r.can_install)], ['todo', S.ag.tbl.todo, n((r) => blocked(r) || r.state === 'invited')]] as [Chip, string, number][]).map(([k, l, c]) => (
              <button key={k} className={'chip' + (chip === k ? ' on' : '')} aria-pressed={chip === k} onClick={() => setChip(chip === k ? null : k)}>{l} <span className="muted">{c}</span></button>
            ))}
          </div>
        </div>
        <label className="ag-sort"><Ic n="filter" s="xs" />{S.ag.tbl.sortBy}
          <select value={by} onChange={(e) => setBy(e.target.value as SortBy)}>
            {(Object.keys(S.ag.tbl.sort) as SortBy[]).map((k) => <option key={k} value={k}>{S.ag.tbl.sort[k]}</option>)}
          </select>
        </label>
      </div>
      <div className="card"><div className="tblwrap">
        <table className="tbl ag-tbl">
          <thead><tr>
            <th>{S.ag.tbl.h.who}</th><th>{S.ag.tbl.h.st}</th><th>{S.ag.tbl.h.rating}</th><th className="r">{S.ag.tbl.h.trips}</th><th className="r">{S.ag.tbl.h.today}</th>
            <th className="r">{S.ag.tbl.h.cash}</th><th>{S.ag.tbl.h.week}</th><th className="r">{S.ag.tbl.h.canc}</th><th>{S.ag.tbl.h.papers}</th><th>{S.ag.tbl.h.consent}</th>
            <th>{S.ag.tbl.h.moto}</th><th>{S.ag.tbl.h.inst}</th>
          </tr></thead>
          <tbody>
            {rows.map(({ a, r }) => (
              <tr key={r.id} className={(selected === r.id ? 'sel ' : '') + (r.state !== 'active' || (!r.live && !r.busy) ? 'dim' : '')} onClick={() => onRider(r.id)}>
                <td><div className="ag-who"><Av name={r.name} size={32} /><div><button className="ag-rowbtn" onClick={(e) => { e.stopPropagation(); onRider(r.id); }} aria-label={`${S.ag.tbl.open} · ${r.name}`}>{r.name}</button><span className="muted">{a.name}</span></div></div></td>
                <td><RiderPill r={r} /></td>
                <td><Stars value={r.rating} count={r.reviews || undefined} /></td>
                <td className="r num">{fmtN(r.trips)}</td>
                <td className="r num"><b>{r.today}</b></td>
                <td className="r num">{r.cash_today ? fmtN(r.cash_today) : <span className="muted">0</span>}</td>
                <td><MiniBars vals={r.week} labels={last7()} /></td>
                <td className="r num">{r.cancelled_30d >= 3 ? <b className="ag-bad">{r.cancelled_30d}</b> : r.cancelled_30d || <span className="muted">0</span>}</td>
                <td>{yes(r.papers_complete, reasonNo(r))}</td>
                <td>{yes(r.consent === 'given', r.consent === 'refused' ? S.ag.tbl.refused : S.ag.tbl.pending)}</td>
                <td className="ag-sm">{[r.vehicle === 'car' ? S.agency.edit.car : S.agency.edit.moto, r.plate].filter(Boolean).join(' · ')}</td>
                <td>{r.can_install ? <Ic n="tool" s="xs" /> : null}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={12}><div className="empty">{S.ag.tbl.none}</div></td></tr>}
          </tbody>
        </table>
      </div></div>
    </>
  );
}
